import * as React from "react"
import { useTranslation } from "react-i18next"
import { Mail, Lock, Loader2 } from "lucide-react"
import { ShadcnFormField } from "@/components/form/ShadcnFormField"
import { ShadcnPasswordField } from "@/components/form/ShadcnPasswordField"
import { Button as ShadcnButton } from "@/components/ui/Button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

export interface LoginFormProps {
  email: string
  password: string
  isLoading?: boolean
  onEmailChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  onForgotPassword?: () => void
  className?: string
}

export const LoginForm: React.FC<LoginFormProps> = ({
  email,
  password,
  isLoading = false,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onForgotPassword,
  className,
}) => {
  const { t } = useTranslation()

  return (
    <form onSubmit={onSubmit} className={cn("space-y-5", className)}>
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
        <ShadcnFormField
          label={t("auth.login.email")}
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={t("auth.login.emailPlaceholder")}
          leftIcon={<Mail className="h-5 w-5" strokeWidth={1.5} />}
          required
          disabled={isLoading}
        />
      </div>

      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
        <ShadcnPasswordField
          label={t("auth.login.password")}
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder={t("auth.login.passwordPlaceholder")}
          leftIcon={<Lock className="h-5 w-5" strokeWidth={1.5} />}
          required
          disabled={isLoading}
        />
      </div>

      <div className="flex items-center justify-between animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
        <div className="flex items-center space-x-2">
          <Checkbox id="remember-me" disabled={isLoading} />
          <Label
            htmlFor="remember-me"
            className="text-sm font-normal text-foreground cursor-pointer"
          >
            {t("auth.login.rememberMe")}
          </Label>
        </div>

        {onForgotPassword && (
          <button
            type="button"
            onClick={onForgotPassword}
            disabled={isLoading}
            className="text-sm font-medium text-accent underline-offset-4 hover:underline hover:opacity-80 transition-opacity disabled:opacity-50"
          >
            {t("auth.login.forgotPassword")}
          </button>
        )}
      </div>

      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 [animation-delay:400ms]">
        <ShadcnButton
          type="submit"
          className="w-full"
          size="lg"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("auth.login.submitting")}
            </>
          ) : (
            t("auth.login.submit")
          )}
        </ShadcnButton>
      </div>
    </form>
  )
}
